/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import {DecisionPoint} from '@/models/scenario/decision.point';

import * as $ from 'jquery';

import 'select2';
import {OptGroupData, OptionData, SearchOptions} from 'select2';
import {Train} from "@/models/scenario/train";
import {TrainDirection} from "@/models/scenario/train.class";
import {Track} from "@/models/scenario/track";
import KeyDownEvent = JQuery.KeyDownEvent;
import KeyUpEvent = JQuery.KeyUpEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class DecisionPointEditor
 *
 * @brief dynamic dropdown list for decision points, also allows for type ahead on decision
 * point name.
 *
 * We use Select2: https://select2.org  for the "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2020
 *
 */
export class DecisionPointEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the selectable values (decision points) */
  public values: DecisionPoint[] = [];

  /** the selected milepost */
  public value: DecisionPoint = null;

  /** the callback to tell agGrid we're done editing */
  private editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  private clickListenter: (e: MouseEvent) => void = null;

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    const de         = this;
    de.editingDone   = params.stopEditing;
    de.values        = [];
    de.value         = params.value;

    /* get the total set of decision points (they are sorted with mile 0 at index 0... */

    // @ts-ignore
    de.values = params.dpList();

    /* set up the Select2 drop down ... */

    de.gui   = document.createElement('div');
    de.gui.tabIndex = 0;

    if (!de.values || (de.values.length === 0)) {

      /* nothing to do, we don't have any decision points */
      return ;
    }

    const   data: any[]         = [];
    let   fakeDP: DecisionPoint = null;

    /*
     * see the note after the loop below; just like for eastbound trains, westbound trains have to have
     * a fake decision point on the east side to allow for selecting that east most interlink as a west
     * side interlink.
     *
     */

    if ('train' in params) {

      // @ts-ignore
      const train: Train = params.train(params.data.id);

      if (train._tc._dir === TrainDirection.WEST) {

        /* shallow clone of the last decision point */

        fakeDP           = de.values[0].clone();

        fakeDP._name     = "EAST SIDE";
        fakeDP._westSide = fakeDP._eastSide;
        fakeDP._eastSide = null;
        fakeDP._isFake   = true;

        data.push({
                id: `dp-${fakeDP._id}-east`,
              text: `EAST SIDE`,
              name: fakeDP._name,
                dp: fakeDP,
          selected: (fakeDP._id === de.value._id) && (fakeDP._name === de.value._name)
        });

      }
    }

    /* walk the decision points, generating a select option for each */

    for (const dp of de.values) {

      /* build this option ... */

      const name = dp._name.trim().toUpperCase();
      const mile = dp._mileage.toFixed(2);
      let selected: boolean = false;

      /* is this the current value? */

      if (de.value instanceof DecisionPoint) {

        selected = (dp._id === de.value._id) && (dp._name === de.value._name);
      }

      /* add the option */

      data.push({
        id: `dp-${dp._id}`,
        text: `${name} - ${mile} mi`,
        name,
        dp,
        selected
      });

    }

    /*
     * if we have a train() method, use it to fetch the Train, and if it's an eastbound train then we
     * duplicate the west most decision point and re-name it "WEST SIDE" and make its east side
     * interlink be the west side interlink.  We have to do this because the user can't select both
     * sides at the same time; so when they are at the west side terminals, they won't actually be
     * able to select the incoming (west side) track, because from that one they can only select
     * the east side (since it's an eastbound train).
     *
     * You can't just switch to using incoming tracks instead of outgoing in the direction of travel,
     * because then you just move the problem to the east side.  The root cause, is that you can't
     * pick tracks from both sides at once.
     *
     * So, we have to pad the decision points, to allow users to select the outermost interlink
     * in the network.
     *
     * This only applies where the decision point editor is used for free-picking original and
     * destinations of trains...so it makes sense to test on the train() method to enable this.
     * that is, in editors like Scenario Browser, they will provide that method on the cell editor
     * parameters object.
     *
     */

    if ('train' in params) {

      // @ts-ignore
      const train: Train = params.train(params.data.id);

      if (train._tc._dir === TrainDirection.EAST) {

        /*
         * we have to add a fake west side decision point, so they can pick the most west interlink...
         * as an east side interlink.
         *
         */

        /* shallow clone of the last decision point */

        fakeDP           = de.values[de.values.length-1].clone();

        fakeDP._name     = "WEST SIDE";
        fakeDP._eastSide = fakeDP._westSide;
        fakeDP._westSide = null;
        fakeDP._isFake   = true;

        data.push({
                id: `dp-${fakeDP._id}-west`,
              text: `WEST SIDE`,
              name: fakeDP._name,
                dp: fakeDP,
          selected: (fakeDP._id === de.value._id) && (fakeDP._name === de.value._name)
        });

      }
    }

    /*
     * because we might have prepended or appended a fake decision point, we might have to move the
     * "current value" to point to one of the fake decision points.
     *
     */

    if (('train' in params) && (fakeDP !== null)) {

      // @ts-ignore
      const train: Train = params.train(params.data.id);

      // @ts-ignore
      const trk: Track = (params.cellStartedEdit === true) ? (params.data.src.track) : ((params.cellStartedEdit === false) ? params.data.dst.track : null);

      if (train._tc._dir == TrainDirection.EAST) {

        if (fakeDP.isEastSideTrack(trk)) {

          /* yup, it's a west most track, west side fake DP must be the selected value */

          this.value = fakeDP;
        }
      }

      if (train._tc._dir == TrainDirection.WEST) {

        if (fakeDP.isWestSideTrack(trk)) {

          /* yup, it's an east most track, east side fake DP must be the selected value */

          this.value = fakeDP;
        }
      }

    }

    /* - - - normal rendering - - - */

    /* insert new HTML for our custom select ... */

    const html = `<select class="work-blox-dp-select"><option></option></select>`;
    $(de.gui).html(html);

    /* now create a Select2 version of it, that supports both selection and type ahead ... */

    const el: JQuery = $(de.gui).find('select.work-blox-dp-select');

    $(el).on('select2:close', () => {

      $(el).trigger('focus');

    });

    $(el).on("select2:closed", function() {

      $(el).select2("open");
    });

    $(el).on('select2:select', (e) => {

      const   selBox: JQuery =  $('input.select2-search__field');

      $(selBox).trigger('focus');

      const datum: any           = e.params.data;

      /* update the current value */

      de.value = datum.dp;

      de.editingDone(true);
    });

    $(el).select2({

      placeholder:  `Select...`,
      allowClear:    false,
      closeOnSelect: true,
      width:         '200px',

      data,

      /* our type-ahead matching function */
      matcher: (args: SearchOptions, datum: OptGroupData | OptionData): any => {

        /* do we have something to match? */

        if ((args?.term ?? '').trim() === '') {

          return datum;
        }

        // @ts-ignore
        const name: string = datum?.name;

        if (!name) {

          /* likely the placeholder option */

          return null;
        }

        if (name.toUpperCase().indexOf(args.term.toUpperCase()) === 0) {

          return datum;
        }

        return null;
      }

    });

    $(el).on("select2:open", function() {

      /*
       * once the dropdown is open, make sure the selection area has the focus so if you press ENTER
       * we can finish editing.
       *
       */

      const selBox: JQuery = $('input.select2-search__field');

      $(selBox).css('width', 'auto');

      if ((params.eventKey ?? '').length === 1) {

        $(selBox).val(params.eventKey);
      }

      $(selBox).trigger('focus');

    });

    setTimeout(() => {

      $(el).select2('open');

      $(de.gui).on('keyup', (e: KeyUpEvent): void => {

        if (e.key !== 'Tab') {

          return ;
        }

        de.editingDone(true);

      });

      $(de.gui).on('keydown', (e: KeyDownEvent) => {

        if ((e.key !== 'Tab') && (e.key !== 'Enter')) {

          return ;
        }

        de.editingDone(true);

        e.preventDefault();
        e.stopPropagation();
      });

    }, 50);

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    de.clickListenter = (e: MouseEvent) => {

      let target = e.target;

      do {

        if ($(target).hasClass(`select2-container`)) {

          /* it's ok, somewhere inside */

          return;
        }

        if ($(target).hasClass('select2-selection__choice')) {

          /* it's ok, somewhere inside */

          return;
        }

        if (target === de.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', de.clickListenter);

      de.editingDone(true);
    };

    document.addEventListener('click', de.clickListenter);
  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): DecisionPoint {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListenter);

    /* deep six the select drop-down */

    const el: JQuery = $(this.gui).find('select.work-blox-dp-select');

    $(el).select2('destroy');

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    /* if there are no decision points...nothing we can do */

    return this.values.length === 0;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
